// File: EmailSideMenu.jsx
import React, { useEffect, useState } from 'react';
import FormTemplate from '../../../forms/FormTemplate';
import { Box, Button, Stack, Typography, Select, MenuItem } from '@mui/material';
import { useParams } from 'react-router-dom';
import ROUTES from '../../../routerfolder/Routes';
import EmailTemplateSelection from './EmailTemplateSelection';
import useAutomations from '../../../hooks/automationhooks/useAutomations';

export default function EmailSideMenu({ onClose, onSubmit, selectedNode, preSelectedTemplate = null }) {
    const COLOR_SURFACE = "#2C2B2D";
    const COLOR_HEADER = "#1E1E1E";
    const COLOR_ACCENT = "#0DA3E2";
    const { brandId } = useParams();
    const { isLoading, emails, handleGetEmails } = useAutomations();
    const [templateSelectionOpen, setTemplateSelectionOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(preSelectedTemplate);

    useEffect(() => {
        handleGetEmails(brandId);
    }, [brandId]);

    useEffect(() => {
        // only run after templates have been fetched
        if (!isLoading && selectedNode?.data?.id) {
            // emails.data is your array of templates
            const match = emails.data.find(
                (tmpl) => tmpl.Id === selectedNode.data.id
            );
            if (match) {
                setSelectedTemplate(match);
            }
        }
    }, [isLoading, emails.data, selectedNode]);

    const handleTemplateSelect = (tmpl) => {
        setSelectedTemplate(tmpl);
        setTemplateSelectionOpen(false);
    };

    const handleSubmitLocal = () => {
        const data = {
            label: "Email Message",
            id: selectedTemplate.Id,
            name: selectedTemplate.Name
        };
        onSubmit(data);
        onClose();
    };

    return (
        <>
            {isLoading && <Typography>Loading Email Templates...</Typography>}
            {!isLoading && (
                <FormTemplate
                    title={selectedNode ? 'Edit Email' : 'Create Email'}
                    isValid={selectedTemplate}
                    onClose={onClose}
                    handleSubmit={handleSubmitLocal}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                color="companyBlue"
                                disabled={!emails.data || !emails.data.length}
                                sx={{
                                    width: "229px",
                                    minHeight: "50px",
                                    borderRadius: "7px",
                                    textTransform: "none",
                                    boxShadow: 0,
                                    color: "#FFFFFF",
                                    px: "12px",
                                    py: "10px",
                                    gap: "10px",
                                }}
                                onClick={() => setTemplateSelectionOpen(true)}
                            >
                                <Typography sx={{ fontFamily: "Assistant, sans-serif", fontWeight: 700, fontSize: "22px", lineHeight: 1, color: "#FFFFFF" }}>
                                    Select Template
                                </Typography>
                            </Button>
                            <Button
                                variant="outlined"
                                sx={{
                                    width: "187px",
                                    minHeight: "50px",
                                    borderRadius: "7px",
                                    borderColor: COLOR_ACCENT,
                                    textTransform: "none",
                                    color: "#FFFFFF",
                                    "&:hover": {
                                        borderColor: COLOR_ACCENT,
                                        bgcolor: "rgba(13, 163, 226, 0.08)",
                                    },
                                }}
                                onClick={() => window.open('#' + ROUTES.TEMPLATES.replace(':TemplateType', 'Email').replace(':brandId', brandId), '_blank', 'noopener')}
                            >
                                <Typography sx={{ fontFamily: "Assistant, sans-serif", fontWeight: 700, fontSize: "18px", lineHeight: 1, color: "#FFFFFF" }}>
                                    Create Template
                                </Typography>
                            </Button>
                        </Stack>

                        {selectedTemplate ? (
                            <Box>
                                <Typography sx={{ color: COLOR_ACCENT, fontWeight: 600, mb: 1 }}>
                                    Selected Template:
                                </Typography>
                                <Box sx={{ border: "1px solid rgba(255,255,255,0.55)", borderRadius: "4px", p: 2, overflowY: 'auto', flexGrow: 1, bgcolor: COLOR_SURFACE }}>
                                    {
                                        <img src={selectedTemplate.ImageSrc} alt={selectedTemplate.Name} style={{ maxWidth: "100%", height: "auto", display: "block" }} />
                                    }
                                </Box>
                            </Box>
                        ) : (
                            <Box>
                                {emails.count ? (
                                    <Typography color="#EB4D4D">No Template Selected</Typography>
                                ) : (
                                    <Typography color="#EB4D4D">No Emails Found</Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </FormTemplate>
            )}

            <EmailTemplateSelection
                open={templateSelectionOpen}
                setSelected={handleTemplateSelect}
                setClosed={() => setTemplateSelectionOpen(false)}
                templates={emails?.data || []}
                refreshTemplates={() => handleGetEmails(brandId)}
            />
        </>
    );
}
